class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.enabled = this.getAttribute('data-enabled');
    this.context = this.getAttribute('data-context');
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('#predictive-search');
    this.closeBtn = this.querySelector('.btn--close-search');
    this.clearBtn = this.querySelector('.btn--clear-input');
    this.screen = this.querySelector('[data-screen]');
    this.SearchModal = this.closest('#SearchModal') || null;
    this.abortController = null; // For cancelling pending requests
    this.minSearchLength = 2; // Minimum characters before searching
    this.selectedIndex = -1; // For keyboard navigation

    // Popular searches - configure these in theme settings or hardcode
    this.popularSearches = ['bullbar', 'suspension', 'lift kit', 'winch', 'led lights', 'roof rack'];

    // Open events
    document.addEventListener('predictive-search:open', e => {
      if (e.detail.context !== this.context) return;
      this.classList.add('is-active');

      // Wait for opening events to finish then apply focus
      setTimeout(() => { this.input.focus(); }, 100);

      document.body.classList.add('predictive-overflow-hidden');
    });

    // listen for class change of 'modal--is-active on this.SearchModal
    if (this.SearchModal) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'class') {
            const modalClass = mutation.target.className;
            if (modalClass.indexOf('modal--is-active') > -1) {
              setTimeout(() => { this.input.focus(); }, 100);
            }
          }
        });
      });

      observer.observe(this.SearchModal, {
        attributes: true
      });
    }

    if (this.enabled === 'false') return;

    // Popular search chips (delegated so handlers survive DOM updates)
    this.predictiveSearchResults.addEventListener('click', (e) => {
      const chip = e.target.closest('.popular-search-link');
      if (!chip) return;

      e.preventDefault();
      e.stopPropagation();

      const term = chip.dataset.term;
      if (!term) return;

      this.classList.add('is-active');
      this.input.value = term;
      this.updateClearButton();
      this.getSearchResults(term);

      if (this.context === 'modal') {
        const modal = document.querySelector('[js-search-modal-wrapper]');
        const cover = document.querySelector('.search-bg-cover');
        modal?.classList.add('is-open');
        cover?.classList.add('is-open');
        this.input.focus({ preventScroll: true });
      }
    });

    // Clear button functionality
    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.clearSearch();
      });
    }

    // Update clear button visibility on input
    this.input.addEventListener('input', () => {
      this.updateClearButton();
    });

    // Show popular searches on focus when empty (defer in modal so open feels instant)
    this.input.addEventListener('focus', () => {
      this.classList.add('is-active');
      if (this.input.value.trim().length < this.minSearchLength) {
        const showPopular = () => this.showPopularSearches();
        if (this.context === 'modal') {
          requestAnimationFrame(showPopular);
        } else {
          showPopular();
        }
      }
    });

    // On typing - add is-active class for both keydown and input events
    this.input.addEventListener('keydown', (e) => {
      this.classList.add('is-active');
      this.handleKeydown(e);
    });

    this.input.addEventListener('input', this.debounce((event) => {
      this.classList.add('is-active'); // Ensure is-active is set for paste/autofill
      this.onChange(event);
    }, 300).bind(this));

    // Close events
    document.addEventListener('predictive-search:close', () => { this.close(); });
    document.addEventListener('keydown', (event) => { if (event.keyCode === 27) this.close(); });
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', e => { e.preventDefault(); this.close(); });
    }
    if (this.screen) {
      this.screen.addEventListener('click', () => { this.close(); });
    }

    // Initial clear button state
    this.updateClearButton();
  }

  updateClearButton() {
    if (this.clearBtn) {
      this.clearBtn.style.display = this.input.value.length > 0 ? 'flex' : 'none';
    }
  }

  clearSearch() {
    this.input.value = '';
    this.updateClearButton();
    this.input.focus();
    this.showPopularSearches();
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  renderSuggestionChip(term, iconSvg) {
    const safe = this.escapeHtml(term);
    if (this.context === 'modal') {
      return `<button type="button" class="popular-search-link" data-term="${safe}" aria-label="Search for ${safe}">
        ${iconSvg}
        <span>${safe}</span>
      </button>`;
    }
    return `<a href="/search?type=product&q=${encodeURIComponent(term)}" class="popular-search-link" data-term="${safe}">
      ${iconSvg}
      <span>${safe}</span>
    </a>`;
  }

  showPopularSearches() {
    const popularIcon = `<svg class="results__icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
    </svg>`;

    const html = `
      <div class="predictive-search-results" id="predictive-search-results">
        <div class="results results--popular">
          <h3 class="h4">Popular Searches</h3>
          <ul role="listbox">
            ${this.popularSearches.map(term => `
              <li role="option">
                ${this.renderSuggestionChip(term, popularIcon)}
              </li>
            `).join('')}
          </ul>
        </div>
      </div>
    `;

    this.predictiveSearchResults.innerHTML = html;
    this.open();
  }

  handleKeydown(e) {
    const results = this.predictiveSearchResults.querySelectorAll('li[role="option"]');
    if (!results.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, results.length - 1);
        this.updateSelectedResult(results);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.updateSelectedResult(results);
        break;
      case 'Enter':
        if (this.selectedIndex >= 0 && results[this.selectedIndex]) {
          e.preventDefault();
          const link = results[this.selectedIndex].querySelector('a');
          if (link) {
            window.location.href = link.href;
          }
        }
        break;
    }
  }

  updateSelectedResult(results) {
    results.forEach((result, index) => {
      if (index === this.selectedIndex) {
        result.classList.add('is-selected');
        result.scrollIntoView({ block: 'nearest' });
      } else {
        result.classList.remove('is-selected');
      }
    });
  }

  onChange() {
    const searchTerm = this.input.value.trim();
    this.selectedIndex = -1; // Reset keyboard selection

    // Don't search if below minimum length - show popular searches instead
    if (searchTerm.length < this.minSearchLength) {
      this.showPopularSearches();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    // Cancel any pending request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    const searchObj = {
      'q': searchTerm,
      'resources[limit]': 6,
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      // Match storefront search (search form uses options[prefix]=last) and include SKU/barcode
      'resources[options][fields]': 'title,product_type,tag,variants.title,variants.sku,variants.barcode,vendor'
    };

    // Storefront search is products-only — keep predictive results aligned
    searchObj['resources[type]'] = window.theme?.settings?.predictiveSearchType || 'product';

    const params = this.paramUrl(searchObj);

    // Add loading state
    this.predictiveSearchResults.innerHTML = '<div class="predictive-search-loading" style="padding: 20px; text-align: center;">Searching...</div>';
    this.open();

    fetch(`${theme.routes.predictiveSearch}?${params}&section_id=search-results`, {
      signal: this.abortController.signal
    })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-search-results');
        if (!section) {
          this.predictiveSearchResults.innerHTML = '';
          this.close();
          return;
        }
        this.predictiveSearchResults.innerHTML = section.innerHTML;
        this.open();
      })
      .catch((error) => {
        // Don't close on abort - user is still typing
        if (error.name === 'AbortError') return;
        if (this.context === 'modal') {
          this.predictiveSearchResults.innerHTML = '<div class="predictive-search-loading" style="padding: 12px;">Unable to load results. Try again.</div>';
          this.open();
          return;
        }
        this.close();
      });
  }

  open() {
    this.predictiveSearchResults.style.display = 'block';
  }

  close() {
    this.predictiveSearchResults.style.display = 'none';
    this.predictiveSearchResults.innerHTML = '';
    this.classList.remove('is-active');
    document.body.classList.remove('predictive-overflow-hidden');

    if (this.context !== 'modal') {
      document.dispatchEvent(new CustomEvent('predictive-search:close-all'));
    }
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  paramUrl(obj) {
    return Object.keys(obj).map(function (key) {
      return key + '=' + encodeURIComponent(obj[key]);
    }).join('&')
  }
}

customElements.define('predictive-search', PredictiveSearch);
